import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { SubPage, Spinner } from "../components/common";
import PhotoUploader from "../components/PhotoUploader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";
import AttributeFields, { PriceUnitChips, ConsentCheck } from "../components/AttributeFields";
import { ConfirmDialog } from "../components/chat/Dialogs";
import { ShareSheet } from "../components/ShareSheet";
import { Camera, Pause, Play, Trash2 } from "lucide-react";

const EMPTY = { category: "", title: "", description: "", photos: [], price: "", zone: "", attributes: {} };
const clean = (a) => Object.fromEntries(Object.entries(a).filter(([, v]) => v !== "" && v != null && !(Array.isArray(v) && !v.length)));

function Field({ label, error, counter, children }) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-sm font-bold text-navy">{label}</span>
        {counter && <span className="text-xs text-slate-400">{counter}</span>}
      </div>
      {children}
      {error && <p className="mt-1 text-xs font-semibold text-alert" data-testid="field-error">{error}</p>}
    </div>
  );
}

const REQUIRED_PHOTOS = 8;

function PhotoProgress({ n, t }) {
  const done = n >= REQUIRED_PHOTOS;
  return (
    <div className={`mb-3 rounded-2xl px-3.5 py-2.5 ${done ? "bg-brand/10" : "bg-alert/5"}`} data-testid="publish-photo-progress">
      <div className="flex items-center justify-between text-sm font-bold">
        <span className={done ? "text-brand" : "text-navy"} data-testid="publish-photo-progress-count">{t("form.photoProgress", { n, m: REQUIRED_PHOTOS })}</span>
        <span className="text-xs font-semibold text-slate-500">{t(done ? "form.photosReady" : "form.photosNeeded", { m: REQUIRED_PHOTOS })}</span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white">
        <div className={`h-full rounded-full transition-[width] duration-300 ${done ? "bg-brand" : "bg-alert"}`} style={{ width: `${Math.min(100, (n / REQUIRED_PHOTOS) * 100)}%` }} />
      </div>
    </div>
  );
}

export default function PublishPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { categories, zones, zoneName, zone, catKind, guardCategory } = useAppData();
  const [form, setForm] = useState(() => {
    const pre = !id && new URLSearchParams(window.location.search).get("category");
    return { ...EMPTY, category: pre && categories.some((c) => c.key === pre) ? pre : "", zone: zone !== "all" ? zone : "" };
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!!id);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [published, setPublished] = useState(null);
  const goListing = () => navigate(`/anuncio/${published.id}`, { replace: true });

  const toggleStatus = async () => {
    const next = status === "active" ? "paused" : "active";
    try {
      await api.post(`/listings/${id}/status`, { status: next });
      setStatus(next);
      toast.success(t(next === "paused" ? "mine.pausedToast" : "mine.reactivatedToast"));
    } catch (e) {
      toast.error(apiError(e, t));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/listings/${id}`);
      toast.success(t("mine.deleted"));
      navigate("/perfil", { replace: true });
    } catch (e) {
      toast.error(apiError(e, t));
    }
  };

  useEffect(() => {
    if (!id) return;
    api.get(`/listings/${id}`).then(({ data }) => {
      if (!data.is_owner) return navigate(`/anuncio/${id}`, { replace: true });
      setForm({ category: data.category, title: data.title, description: data.description, photos: data.photos,
        price: data.price ?? "", zone: data.zone, attributes: data.attributes || {} });
      setStatus(data.status);
      setLoading(false);
    }).catch(() => navigate("/mis-anuncios", { replace: true }));
  }, [id, navigate]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const kind = catKind(form.category);
  const attrs = form.attributes;
  const setAttrs = set("attributes");
  const pickCategory = (c) => guardCategory(c, () => setForm((f) => ({ ...f, category: c, attributes: f.category === c ? f.attributes : {} })));
  const builtTitle = attrs.name?.trim() ? (attrs.age ? `${attrs.name.trim()}, ${attrs.age}` : attrs.name.trim()) : "";

  const validate = () => {
    const e = {};
    if (!form.category) e.category = t("err.required");
    if (kind === "contactos") {
      if (!attrs.name?.trim()) e.name = t("err.required");
      if (attrs.age !== undefined && attrs.age !== "" && attrs.age < 18) e.age = t("err.ageMin");
      if (!attrs.looking_for?.length) e.looking_for = t("err.lookingFor");
      if (!attrs.content_consent) e.consent = t("err.consent");
      if (!attrs.real_photo_consent) e.realPhoto = t("err.consent");
    } else if (!form.title.trim()) e.title = t("err.required");
    if (kind === "property" && !attrs.operation) e.operation = t("err.required");
    if (kind !== "contactos" && !form.description.trim()) e.description = t("err.required");
    if (form.photos.length < 1) e.photos = t("err.photosMin");
    if (!form.zone) e.zone = t("err.required");
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    const a = clean({ ...attrs, name: attrs.name?.trim() });
    const isC = kind === "contactos";
    const payload = { ...form, title: isC ? builtTitle : form.title.trim(), description: form.description.trim(),
      price: isC || form.price === "" ? null : Number(form.price), attributes: kind === "generic" ? {} : a };
    try {
      const { data } = id ? await api.put(`/listings/${id}`, payload) : await api.post("/listings", payload);
      toast.success(t(id ? "publish.saved" : "publish.published"));
      if (!id && !isC) {
        setBusy(false);
        return setPublished(data);
      }
      navigate(`/anuncio/${data.id}`, { replace: true });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "contactos_profile_exists") {
        toast(t("contact.profileExists"));
        navigate(`/editar/${d.listing_id}`, { replace: true });
      } else toast.error(apiError(e, t));
    }
    setBusy(false);
  };

  if (loading) return <div className="flex min-h-[100dvh] items-center justify-center"><Spinner /></div>;

  return (
    <SubPage title={t(id ? "publish.editTitle" : "publish.title")} testid="publish-page">
      {id && kind === "contactos" && form.photos.length < REQUIRED_PHOTOS && (
        <div className="mb-4 flex items-center gap-2.5 rounded-2xl bg-alert/10 px-4 py-3 text-sm font-bold text-alert" data-testid="publish-photos-missing-banner">
          <Camera className="h-5 w-5 shrink-0" />
          {t("form.photosMissing", { x: REQUIRED_PHOTOS - form.photos.length })}
        </div>
      )}
      <form onSubmit={submit} className="space-y-5 rounded-3xl bg-white p-5 shadow-[0_4px_20px_rgba(13,42,107,0.06)] sm:p-7" noValidate>
        <Field label={t("publish.category")} error={errors.category}>
          <Select value={form.category} onValueChange={pickCategory}>
            <SelectTrigger className="field" data-testid="publish-category-select"><SelectValue placeholder={t("publish.chooseCategory")} /></SelectTrigger>
            <SelectContent className="max-h-72">
              {categories.map((c) => (
                <SelectItem key={c.key} value={c.key} data-testid={`publish-category-option-${c.key}`}>
                  {lang === "en" ? c.name_en : c.name_es}{c.adult ? " (+18)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {kind === "contactos" ? (
          <>
            <AttributeFields kind={kind} attrs={attrs} onChange={setAttrs} errors={errors} />
            <div className="rounded-2xl bg-mist px-4 py-3 text-sm" data-testid="publish-title-preview">
              <span className="text-slate-500">{t("form.titlePreview")}: </span>
              <span className="font-extrabold text-navy">{builtTitle || "—"}</span>
            </div>
          </>
        ) : (
          <Field label={t("publish.titleLabel")} error={errors.title} counter={`${form.title.length}/100`}>
            <input className="field" maxLength={100} value={form.title} placeholder={t("publish.titlePh")}
              onChange={(e) => set("title")(e.target.value)} data-testid="publish-title-input" />
          </Field>
        )}
        <Field label={t(kind === "contactos" ? "form.about" : "publish.description")} error={errors.description} counter={`${form.description.length}/2000`}>
          <textarea className="field min-h-[140px] resize-y" maxLength={2000} value={form.description}
            placeholder={t(kind === "contactos" ? "form.aboutPh" : "publish.descriptionPh")}
            onChange={(e) => set("description")(e.target.value)} data-testid="publish-description-input" />
          {kind === "contactos" && <p className="mt-1.5 text-xs text-slate-500" data-testid="publish-about-hint">{t("form.aboutHint")}</p>}
        </Field>
        {kind !== "contactos" && kind !== "generic" && (
          <div className="rounded-2xl border border-slate-100 p-4" data-testid="publish-attributes">
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-navy/60">{t("form.details")}</p>
            <AttributeFields kind={kind} attrs={attrs} onChange={setAttrs} errors={errors} />
          </div>
        )}
        <Field label={`${t("publish.photos")} (${form.photos.length}/8)`} error={errors.photos}>
          <p className="mb-2.5 text-xs text-slate-500">{t("publish.photosHint")}</p>
          {kind === "contactos" && <PhotoProgress n={form.photos.length} t={t} />}
          <PhotoUploader photos={form.photos} onChange={set("photos")} />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          {kind !== "contactos" && (
            <Field label={t("publish.price")}>
              <input className="field" type="number" min="0" step="0.01" inputMode="decimal" value={form.price}
                placeholder={t("publish.pricePh")} onChange={(e) => set("price")(e.target.value)} data-testid="publish-price-input" />
              {kind === "property" && (
                <div className="mt-2.5">
                  <PriceUnitChips value={attrs.price_unit} onChange={(v) => setAttrs({ ...attrs, price_unit: v })} />
                </div>
              )}
            </Field>
          )}
          <Field label={t("publish.zone")} error={errors.zone}>
            <Select value={form.zone} onValueChange={set("zone")}>
              <SelectTrigger className="field" data-testid="publish-zone-select"><SelectValue placeholder={t("publish.chooseZone")} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {zones.filter((z) => z.key !== "all").map((z) => (
                  <SelectItem key={z.key} value={z.key} data-testid={`publish-zone-option-${z.key}`}>{zoneName(z.key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        {kind === "contactos" && (
          <div className="space-y-3">
            <ConsentCheck checked={!!attrs.content_consent} onChange={(v) => setAttrs({ ...attrs, content_consent: v })} error={errors.consent} />
            <ConsentCheck checked={!!attrs.real_photo_consent} onChange={(v) => setAttrs({ ...attrs, real_photo_consent: v })}
              error={errors.realPhoto} label="form.realPhoto" testid="publish-real-photo-checkbox" />
          </div>
        )}
        <button type="submit" disabled={busy} className="btn-primary w-full py-4 text-lg" data-testid="publish-submit-btn">
          {busy ? <Spinner className="border-white/40 border-t-white" /> : t(id ? "publish.save" : "publish.submit")}
        </button>
      </form>
      {id && kind === "contactos" && status && (
        <div className="mt-4 flex gap-2.5" data-testid="publish-profile-actions">
          <button onClick={toggleStatus} className="btn-outline flex-1 py-3" data-testid={status === "active" ? "publish-pause-btn" : "publish-reactivate-btn"}>
            {status === "active" ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />} {t(status === "active" ? "mine.pause" : "mine.reactivate")}
          </button>
          <button onClick={() => setConfirmDelete(true)} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-alert/40 py-3 font-bold text-alert transition-colors hover:bg-alert/10" data-testid="publish-delete-btn">
            <Trash2 className="h-4 w-4" /> {t("mine.delete")}
          </button>
        </div>
      )}
      <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title={t("mine.deleteTitle")} sub={t("mine.deleteSub")}
        confirmLabel={t("mine.confirmDelete")} onConfirm={remove} testid="publish-delete-dialog" />
      <ShareSheet open={!!published} listing={published} published
        onOpenChange={(o) => !o && goListing()} onView={goListing} onLater={goListing} />
    </SubPage>
  );
}
